import { Column, Pie } from "@ant-design/charts";
import {
  BarChartOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  // ProductOutlined,
  ProjectOutlined,
  StockOutlined,
  TagOutlined,
} from "@ant-design/icons";

import { Button, Card, Col, DatePicker, Row, Spin, Typography } from "antd";
import { getData } from "apiServices/common";
import moment from "moment";
import { useEffect, useState } from "react";

const { Title, Text } = Typography;

export default function DashboardPNL() {
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch dashboard data from API
  const fetchDashboardData = async (filterDate) => {
    try {
      setLoading(true);
      setError(null);
      const formattedDate = filterDate.format("YYYY-MM-DD");
      const response = await getData(
        `/api/pnl-report/dashboard?filter_date=${formattedDate}`,
      );

      if (response?.data) {
        setDashboardData(response?.data?.data);
      } else {
        setError("No data found for the selected date");
        setDashboardData(null);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data. Please try again.");
      setDashboardData(null);
      setLoading(false);
    }
  };

  // Load data when component mounts or date changes
  // useEffect(() => {
  //   fetchDashboardData(selectedDate);
  // }, [selectedDate]);

  // Handle date change
  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  // Handle refresh button
  const handleRefresh = () => {
    fetchDashboardData(selectedDate);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return "$0";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Performance metrics cards data
  const performanceCards = [
    {
      title: "Production",
      value: formatNumber(dashboardData?.production?.today || 0),
      previousValue: formatNumber(dashboardData?.production?.previous || 0),
      icon: <TagOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "Average CM (USD)",
      value: formatCurrency(dashboardData?.["average-cm-usd"]?.today || 0),
      previousValue: formatCurrency(
        dashboardData?.["average-cm-usd"]?.previous || 0,
      ),

      icon: <DollarOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "CM Revenue (USD)",
      value: formatCurrency(dashboardData?.["cm-revenue-usd"]?.today || 0),
      previousValue: formatCurrency(
        dashboardData?.["cm-revenue-usd"]?.previous || 0,
      ),

      icon: <DollarOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "CM Revenue (BDT)",
      value: formatNumber(dashboardData?.["cm-revenue-bdt"]?.today || 0),
      previousValue: formatNumber(
        dashboardData?.["cm-revenue-bdt"]?.previous || 0,
      ),

      icon: <BarChartOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "Total Expenses",
      value: formatNumber(dashboardData?.["total-expenses"]?.today || 0),
      previousValue: formatNumber(
        dashboardData?.["total-expenses"]?.previous || 0,
      ),

      icon: <ProjectOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "Net Profit/Loss",
      value: formatNumber(dashboardData?.["net-profit-loss"]?.today || 0),
      previousValue: formatNumber(
        dashboardData?.["net-profit-loss"]?.previous || 0,
      ),
      icon: <StockOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
    {
      title: "Overtime (OT)",
      value: formatNumber(
        parseFloat(dashboardData?.["over-time"]?.today || "0"),
      ),
      previousValue: formatNumber(
        parseFloat(dashboardData?.["over-time"]?.previous || "0"),
      ),
      color: "#1890ff",
      icon: (
        <ClockCircleOutlined style={{ fontSize: "24px", color: "white" }} />
      ),
    },
    {
      title: "Qty for Breakeven",
      value: formatNumber(dashboardData?.["qty-for-breakdown"]?.today || 0),
      previousValue: formatNumber(
        dashboardData?.["qty-for-breakdown"]?.previous || 0,
      ),
      // icon: <ProductOutlined style={{ fontSize: "24px", color: "white" }} />,
      color: "#1890ff",
    },
  ];

  // Bar chart data for CM Revenue & Total Cost
  const barChartData = [
    {
      category: "Today",
      type: "CM Revenue",
      value: dashboardData?.barchart?.today?.cm_revenue || 0,
    },
    {
      category: "Today",
      type: "Total Cost",
      value: dashboardData?.barchart?.today?.cost || 0,
    },
    {
      category: "MTD",
      type: "CM Revenue",
      value: dashboardData?.barchart?.mtd?.cm_revenue || 0,
    },
    {
      category: "MTD",
      type: "Total Cost",
      value: dashboardData?.barchart?.mtd?.cost || 0,
    },
  ];

  const barChartConfig = {
    data: barChartData,
    xField: "category",
    yField: "value",
    seriesField: "type",
    dodgePadding: 4,
    intervalPadding: 20,
    color: ["#1890ff", "#ff7875"],
    label: {
      position: "top",
      formatter: (datum) => formatCurrency(datum),
    },

    legend: {
      position: "top",
    },
  };

  // Pie chart data for Total Expense Breakdown
  const pieChartData = [
    {
      type: "Manufacturing Expenses",
      value: parseFloat(
        dashboardData?.pichart?.["manufacturing-expense"] || "0",
      ),
    },
    {
      type: "Office & Administrative Expenses",
      value: parseFloat(dashboardData?.pichart?.["office-expense"] || "0"),
    },
    {
      type: "Selling & Marketing Expenses",
      value: parseFloat(dashboardData?.pichart?.["selling-expense"] || "0"),
    },
    {
      type: "Financial Expenses",
      value: parseFloat(dashboardData?.pichart?.["financial-expense"] || "0"),
    },
    {
      type: "Income Tax (Source Tax)",
      value: parseFloat(dashboardData?.pichart?.["income-tax"] || "0"),
    },
  ];

  const pieChartConfig = {
    appendPadding: 10,
    data: pieChartData,
    angleField: "value",
    colorField: "type",
    radius: 0.8,
    innerRadius: 0.6,
    interactions: [
      {
        type: "element-active",
      },
    ],
    legend: {
      position: "bottom",
    },

    statistic: {
      title: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        content: "Total\nExpenses",
      },
      content: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        content: "100%",
      },
    },
  };

  return (
    <div style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={2} style={{ color: "#1890ff", margin: 0 }}>
            PERFORMANCE DASHBOARD
          </Title>
          <Text type="secondary" style={{ fontSize: "14px" }}>
            Data for: {selectedDate.format("MMMM DD, YYYY")}
          </Text>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            placeholder="Select Date"
            style={{ width: 150 }}
          />
          <Button type="primary" onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && !error && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40vh",
            fontSize: "18px",
            backgroundColor: "white",
            borderRadius: "12px",
            margin: "24px 0",
          }}
        >
          <Spin size="large" tip="Loading dashboard data..." />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40vh",
            fontSize: "18px",
            color: "#ff4d4f",
            flexDirection: "column",
            gap: "16px",
            backgroundColor: "white",
            borderRadius: "12px",
            margin: "24px 0",
          }}
        >
          <div>{error}</div>
          <Button type="primary" onClick={handleRefresh}>
            Retry
          </Button>
        </div>
      )}

      {/* Dashboard Content - Only show when not loading and no error */}
      {!loading && !error && dashboardData && (
        <>
          {/* Performance Cards - Today */}
          <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
            <Col span={24}>
              <div
                style={{
                  backgroundColor: "#e6f7ff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  border: "1px solid #91d5ff",
                }}
              >
                <Text strong style={{ color: "#1890ff" }}>
                  TODAY'S PERFORMANCE
                </Text>
              </div>
            </Col>

            {performanceCards.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={4} key={`today-${index}`}>
                <Card
                  style={{
                    borderRadius: "12px",
                    background: card.color,
                    border: "none",
                    color: "white",
                  }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {card.title}
                      </Text>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          margin: "4px 0",
                          color: "white",
                        }}
                      >
                        {card.value}
                      </div>
                    </div>
                    <div style={{ marginLeft: "12px" }}>{card.icon}</div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Performance Cards - Previous Day */}
          <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
            <Col span={24}>
              <div
                style={{
                  backgroundColor: "#fff2e8",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  border: "1px solid #ffbb96",
                }}
              >
                <Text strong style={{ color: "#fa8c16" }}>
                  PREVIOUS DAY PERFORMANCE
                </Text>
              </div>
            </Col>
            {performanceCards.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={4} key={`previous-${index}`}>
                <Card
                  style={{
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #ffa940 0%, #fa8c16 100%)",
                    border: "none",
                    color: "white",
                  }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {card.title}
                      </Text>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          margin: "4px 0",
                          color: "white",
                        }}
                      >
                        {card.previousValue}
                      </div>
                    </div>
                    <div style={{ marginLeft: "12px" }}>{card.icon}</div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]}>
            {/* CM Revenue & Total Cost Chart */}
            <Col xs={24} lg={14}>
              <Card
                title="CM Revenue & Total Cost"
                style={{ borderRadius: "12px" }}
              >
                <Column {...barChartConfig} height={300} />
              </Card>
            </Col>

            {/* Total Expense Breakdown */}
            <Col xs={24} lg={10}>
              <Card
                title="Total Expense Breakdown"
                style={{ borderRadius: "12px" }}
              >
                <Pie {...pieChartConfig} height={300} />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
